// Amplitude Analytics Driver using @amplitude/analytics-browser
import * as amplitude from '@amplitude/analytics-browser';

class AmplitudeAnalyticsDriver {
  static #isInitialized = false;

  static #userId = null;

  static #tries = 0;

  /**
   * Initializes the Amplitude SDK.
   *
   * @param {String} apiKey - Your Amplitude API Key.
   * @param {Object} [options={}] - Additional Amplitude configuration options (e.g., trackingOptions).
   * @returns {boolean} - True if initialization was successful or already done, false otherwise.
   */
  static init(apiKey, options = {}) {
    if (typeof window === 'undefined') {
      console.error('AmplitudeAnalyticsDriver: Cannot initialize outside browser environment.');
      return false;
    }

    if (this.#isInitialized) {
      console.warn('AmplitudeAnalyticsDriver: Already initialized.');
      return true; // Prevent re-initialization
    }

    if (!apiKey) {
      console.error('AmplitudeAnalyticsDriver: API Key is required for initialization.');
      return false;
    }

    try {
      // Default options + EU Server Zone
      const config = {
        serverZone: 'EU',
        defaultTracking: false, // Disable default event tracking
        ...options, // Pass other options like trackingOptions here
      };

      // Call the init function directly from the imported module
      amplitude.init(apiKey, undefined, config); // Use undefined for userId if not setting it here

      this.#isInitialized = true;
      console.log('Amplitude initialized with EU server zone.', config);
      return true;
    } catch (error) {
      console.error('AmplitudeAnalyticsDriver: Failed to initialize:', error);
      this.#isInitialized = false;
      return false;
    }
  }

  /**
   * Checks if the Amplitude SDK instance is initialized.
   * @returns {boolean}
   */
  static isAvailable() {
    if (!this.#isInitialized) {
      console.warn('AmplitudeAnalyticsDriver: SDK not initialized. Call init() first.');
      return false;
    }
    // No need for getInstance check with the new SDK's direct functions
    return true;
  }

  /**
   * Tracks an event with Amplitude.
   *
   * @param {String} eventName - The name of the event to track.
   * @param {Object} [properties={}] - Optional properties to associate with the event.
   * @returns {boolean} - True if the event was tracked, false otherwise.
   */
  static trackEvent(eventName, properties = {}) {
    if (!this.isAvailable()) {
      return false;
    }

    try {
      // Call the track function directly from the imported module
      amplitude.track(eventName, properties);

      // console.log(`Amplitude Event: ${eventName}`, properties); // Optional: for debugging
      return true;
    } catch (error) {
      console.error(`AmplitudeAnalyticsDriver: Failed to track event '${eventName}':`, error);
      return false;
    }
  }

  static setUserId(userId) {
    if (!this.isAvailable()) {
      if (this.#tries >= 5) {
        return false;
      }

      setTimeout(() => this.setUserId(userId), 2000);
      this.#tries += 1;
      return false;
    }

    if (this.#userId === userId) {
      return false;
    }

    try {
      amplitude.setUserId(String(userId));
      this.#userId = userId;
      return true;
    } catch (error) {
      console.error('AmplitudeAnalyticsDriver: Failed to set user ID:', error);
      return false;
    }
  }
}

// Export a static class or an instance depending on preference
export default AmplitudeAnalyticsDriver;
